import * as React from 'react'
import { Menu } from 'piral'
import { Focusable } from '@medialib/styleguide'

/**
 * The header's overflow menu (⋮) — secondary destinations that don't earn a
 * permanent header slot: external sites, settings, and so on.
 *
 * The shell owns only the chrome (the button, the panel, open/close and focus).
 * The entries come from Piral's `admin` menu type, so pilets contribute them with
 * `registerMenu(..., { type: 'admin' })` exactly as they already do for the main
 * nav — the shell never has to know what is in here.
 */
const KebabMenu: React.FC = () => {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const hasOpened = React.useRef(false)

  // Closing unmounts the entries, which would strand the remote on nothing — put
  // focus back on the button that opened them. `hasOpened` keeps this from firing
  // on mount, where nothing should be focused at all.
  React.useEffect(() => {
    if (open) {
      hasOpened.current = true
      return
    }
    if (hasOpened.current) buttonRef.current?.focus()
  }, [open])

  // Opening moves straight to the first entry, so a remote doesn't have to hunt
  // for the panel it just summoned.
  React.useEffect(() => {
    if (!open) return
    const first = rootRef.current?.querySelector('.KebabMenu__panel a, .KebabMenu__panel button')
    ;(first as HTMLElement | null)?.focus()
  }, [open])

  React.useEffect(() => {
    if (!open) return
    // Escape is not in Norigin's keymap, so it reaches us untouched.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  return (
    <div className='KebabMenu' ref={rootRef} data-open={open}>
      <Focusable className='mlsg-focusable--inline'>
        <button
          type='button'
          ref={buttonRef}
          className='KebabMenu__button'
          aria-haspopup='menu'
          aria-expanded={open}
          aria-label='More options'
          onClick={() => setOpen(value => !value)}
        >
          <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true' focusable='false'>
            <path
              fill='currentColor'
              d='M12 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4z'
            />
          </svg>
        </button>
      </Focusable>
      {open
        ? (
          <div className='KebabMenu__panel'>
            <Menu type='admin' />
          </div>
          )
        : null}
    </div>
  )
}

export default KebabMenu
